/**
 * Putting a theme on screen, and keeping it.
 *
 * The derived properties are written onto the root element, where they
 * outrank every stylesheet value, and cached in localStorage so the tiny
 * script in index.html can restore them before the app mounts — a theme
 * that flashes back to the default on every load is not a theme. The
 * choice itself is kept on the server per user, so it follows them to the
 * next browser, and mirrored in the same cache for the first paint.
 */

import { ref, watch } from "vue";
import { api } from "@/lib/api";
import {
  DEFAULT_THEME,
  presetById,
  variablesToPaint,
  type Appearance,
  type AppearanceOverrides,
  type Mode,
} from "@/lib/appearance";
import { resolvedTheme } from "@/lib/theme";

export const APPEARANCE_KEY = "genie.appearance";
/** The derived properties for the last applied theme, for index.html's first paint. */
export const APPEARANCE_CACHE_KEY = "genie.appearance.vars";

function stored(): Appearance {
  try {
    const raw = localStorage.getItem(APPEARANCE_KEY);
    const parsed = raw ? (JSON.parse(raw) as Partial<Appearance>) : null;
    if (parsed && typeof parsed.theme === "string") {
      return { theme: parsed.theme, overrides: parsed.overrides && typeof parsed.overrides === "object" ? parsed.overrides : {} };
    }
  } catch {
    /* nothing kept */
  }
  return { theme: DEFAULT_THEME, overrides: {} };
}

export const appearance = ref<Appearance>(stored());
/** True when an administrator has set the look for everyone; choosing is off. */
export const appearanceLocked = ref(false);
/** Whose choice is on screen: the user's, the organisation's, or the app's own default. */
export const appearanceSource = ref<"user" | "org" | "default">("user");

let written: string[] = [];
/** What is on screen now — the kept choice, or a preview of another. */
let painted: Appearance = appearance.value;

/**
 * Desk, as shipped, is the standard: frappe-ui's own palette, type and
 * shape, and the shell's own colours, exactly as their stylesheets define
 * them. It is painted by writing nothing — every derived value is an
 * approximation of a stylesheet that is already right. Any other theme,
 * or Desk with a change on it, is painted in full: the shell, the console,
 * frappe-ui's scales, the charts, shape and type.
 */
/** Write one mode's derivation onto the root element. */
export function paintAppearance(value: Appearance, mode: Mode): void {
  painted = value;
  const root = document.documentElement;
  const vars = variablesToPaint(value, mode);
  for (const name of written) if (!(name in vars)) root.style.removeProperty(name);
  for (const [name, v] of Object.entries(vars)) root.style.setProperty(name, v);
  written = Object.keys(vars);
  root.setAttribute("data-skin", value.theme);
  root.setAttribute("data-density", vars["--density"] === "0.85" ? "compact" : "comfortable");
  root.setAttribute("data-chrome", presetById(value.theme).chrome === "dark" ? "dark" : "same");
  try {
    localStorage.setItem(APPEARANCE_CACHE_KEY, JSON.stringify({ mode, theme: value.theme, vars }));
  } catch {
    /* the next load paints a moment later, that is all */
  }
}

function persistLocally(value: Appearance): void {
  try {
    localStorage.setItem(APPEARANCE_KEY, JSON.stringify(value));
  } catch {
    /* ignore */
  }
}

/** Show a theme without keeping it — the gallery's hover, the customiser's live edit. */
export function previewAppearance(value: Appearance): void {
  paintAppearance(value, resolvedTheme.value);
}

/** Variables that move layout: fonts, the type scale, radii, density. Everything else is paint. */
const SHAPE = /^--(font-|type-scale|radius|density)/;

/**
 * Show a theme's colours without changing the page's shape.
 *
 * The gallery previews a theme when the cursor rests on its card. A full
 * paint swaps fonts, the type scale and every radius at the same time, and
 * a page that reflows under a moving cursor is the jerk an operator feels
 * when sweeping across the cards. Colour changes cost a repaint and no
 * reflow, so the peek paints those and leaves the shape as the kept theme
 * has it; the shape follows only when the card is chosen.
 */
export function peekColours(value: Appearance): void {
  const root = document.documentElement;
  const vars = variablesToPaint(value, resolvedTheme.value);
  for (const [name, v] of Object.entries(vars)) {
    if (!SHAPE.test(name)) root.style.setProperty(name, v);
  }
  root.setAttribute("data-chrome", presetById(value.theme).chrome === "dark" ? "dark" : "same");
}

/**
 * Keep a theme: on screen, in this browser, and on the server for this user.
 * Under a lock nothing is kept — the organisation's look is restored instead.
 */
export async function setAppearance(value: Appearance): Promise<boolean> {
  if (appearanceLocked.value) {
    restoreAppearance();
    return false;
  }
  appearance.value = { theme: value.theme, overrides: { ...value.overrides } };
  appearanceSource.value = "user";
  persistLocally(appearance.value);
  paintAppearance(appearance.value, resolvedTheme.value);
  try {
    await api.saveAppearance(appearance.value);
  } catch {
    /* kept locally; the server catches up on the next save */
  }
  return true;
}

export function setThemeById(theme: string, overrides: AppearanceOverrides = {}): Promise<boolean> {
  return setAppearance({ theme, overrides });
}

/** Paint what is known now, and follow the mode switch from then on. */
export function initAppearance(): void {
  paintAppearance(appearance.value, resolvedTheme.value);
  // A preview stays a preview across a light/dark flip; a kept choice stays kept.
  watch(resolvedTheme, (mode) => paintAppearance(painted, mode));
}

/** Back from a preview to the kept choice. */
export function restoreAppearance(): void {
  paintAppearance(appearance.value, resolvedTheme.value);
}

/** After sign-in: the server's copy wins over the browser's. */
export async function loadAppearance(): Promise<void> {
  try {
    const remote = await api.appearance();
    if (remote && typeof remote.theme === "string") {
      appearance.value = { theme: remote.theme, overrides: remote.overrides ?? {} };
      appearanceLocked.value = remote.locked === true;
      appearanceSource.value = remote.source ?? "user";
      persistLocally(appearance.value);
      paintAppearance(appearance.value, resolvedTheme.value);
    }
  } catch {
    /* the local choice stands */
  }
}
